import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, ArrowRight, Tag } from 'lucide-react';
import { PublicPageLayout } from '../components/PublicPageLayout';
import { api } from '../services/api';

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--primary);
  background: none;
  border: none;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 1rem;
`;

const ArticleCard = styled.article`
  background: var(--bg-card);
  border-radius: 20px;
  padding: 28px;
  border: 1px solid rgba(0, 212, 170, 0.12);
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.2);

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const ArticleTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-light);
  margin: 0.75rem 0 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const ArticleExcerpt = styled.p`
  color: var(--text-muted);
  font-size: 1rem;
  margin-bottom: 1.25rem;
`;

const CoverFrame = styled.div`
  border-radius: 18px;
  border: 1px solid rgba(0, 212, 170, 0.16);
  background: linear-gradient(135deg, rgba(0, 212, 170, 0.18), rgba(8, 145, 178, 0.12));
  padding: 12px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CoverImage = styled.img`
  width: 100%;
  max-height: 360px;
  object-fit: contain;
  border-radius: 12px;
  display: block;
`;

const CoverPlaceholder = styled.div`
  width: 100%;
  height: 240px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(0, 212, 170, 0.2), rgba(8, 145, 178, 0.2));
`;

const Meta = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 0.85rem;
  color: #94a3b8;
  margin-bottom: 1.25rem;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--primary);
  background: rgba(0, 212, 170, 0.12);
  padding: 4px 10px;
  border-radius: 999px;
  width: fit-content;
`;

const Content = styled.div`
  color: var(--text-muted);
  font-size: 1rem;
  line-height: 1.7;

  p {
    margin-bottom: 1rem;
  }

  img {
    max-width: 100%;
    border-radius: 12px;
    margin: 16px 0;
  }
`;

const EmptyState = styled.div`
  background: var(--bg-card);
  border-radius: 18px;
  padding: 26px;
  border: 1px solid rgba(0, 212, 170, 0.12);
  color: var(--text-muted);
`;

const PostNav = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-top: 24px;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid rgba(0, 212, 170, 0.2);
  background: rgba(10, 14, 23, 0.6);
  color: var(--text-light);
  font-weight: 600;
  cursor: pointer;

  span {
    display: block;
    color: var(--text-muted);
    font-size: 0.85rem;
  }
`;

type BlogPostData = {
  id: number;
  title: string;
  slug: string;
  excerpt?: string;
  content?: string;
  tags?: string[];
  cover_image?: string;
  published_at?: string;
  created_at?: string;
};

export const BlogPostPage: React.FC = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [posts, setPosts] = useState<BlogPostData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadPost = async () => {
      if (!slug) return;
      try {
        setLoading(true);
        const response = await api.get(`/blog/posts/${slug}`);
        if (response.data?.success && response.data.data) {
          setPost(response.data.data);
        } else {
          setError(true);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [slug]);

  useEffect(() => {
    const loadList = async () => {
      try {
        const response = await api.get('/blog/posts');
        if (response.data?.success && Array.isArray(response.data.data)) {
          setPosts(response.data.data);
        }
      } catch {
        setPosts([]);
      }
    };

    loadList();
  }, []);

  const dateLabel = post?.published_at || post?.created_at;
  const currentIndex = posts.findIndex(item => item.slug === slug);
  const nextPost = currentIndex > 0 ? posts[currentIndex - 1] : null;
  const prevPost = currentIndex >= 0 && currentIndex < posts.length - 1 ? posts[currentIndex + 1] : null;

  return (
    <PublicPageLayout
      title={post?.title || 'Blog PontoFiel'}
      subtitle={post?.excerpt || 'Conteudo completo do blog.'}
      eyebrow="Blog"
    >
      <BackButton type="button" onClick={() => navigate('/blog')}>
        <ArrowLeft size={16} /> Voltar
      </BackButton>

      {loading ? (
        <EmptyState>Carregando artigo...</EmptyState>
      ) : error || !post ? (
        <EmptyState>Artigo nao encontrado.</EmptyState>
      ) : (
        <ArticleCard>
          <Badge><Tag size={14} /> {post.tags?.[0] || 'Geral'}</Badge>
          <ArticleTitle>{post.title}</ArticleTitle>
          {post.excerpt && <ArticleExcerpt>{post.excerpt}</ArticleExcerpt>}
          <CoverFrame>
            {post.cover_image ? (
              <CoverImage src={post.cover_image} alt={post.title} />
            ) : (
              <CoverPlaceholder />
            )}
          </CoverFrame>
          <Meta>
            <span>{dateLabel ? new Date(dateLabel).toLocaleDateString('pt-BR') : ''}</span>
            {post.tags?.slice(1).map(tag => (
              <span key={tag}>{tag}</span>
            ))}
          </Meta>
          <Content dangerouslySetInnerHTML={{ __html: post.content || '' }} />
          <PostNav>
            {prevPost && (
              <NavButton type="button" onClick={() => navigate(`/blog/${prevPost.slug}`)}>
                <div>
                  <span>Anterior</span>
                  {prevPost.title}
                </div>
                <ArrowLeft size={16} />
              </NavButton>
            )}
            {nextPost && (
              <NavButton type="button" onClick={() => navigate(`/blog/${nextPost.slug}`)}>
                <div>
                  <span>Proximo</span>
                  {nextPost.title}
                </div>
                <ArrowRight size={16} />
              </NavButton>
            )}
          </PostNav>
        </ArticleCard>
      )}
    </PublicPageLayout>
  );
};
